import { ScenarioOutletContext, Intervention } from '@/types';
import {
  Link,
  useOutletContext,
  useNavigate,
  Navigate,
} from 'react-router-dom';
import { useCallback, useState } from 'react';
import { useInterventionKey } from '@/hooks/use-intervention-key';
import { useTheme } from 'styled-components';
import {
  ConfirmationDialog,
  DropdownMenu,
  DropdownMenuButton,
  DropdownMenuItem,
  DropdownMenuList,
  DropdownMenuSeparator,
  Button,
  UtilityButton,
  Separator,
  Container,
  Frame,
  Content,
  Header,
  Toolbar,
  ToolbarGroup,
  ToolbarItem,
  H2,
} from '@kinesis/bungle';
import { InterventionEdit } from '@/components/intervention-edit';
import { CopyIntervention } from '@/components/copy-intervention';
import { useFlag } from '@/hooks/use-flag';

const ScenarioAssumptions = () => {
  const { scenario, removeIntervention, updateIntervention } =
    useOutletContext<ScenarioOutletContext>();
  const theme = useTheme() as any;
  const enableCopy = useFlag('community.copy');
  const navigate = useNavigate();
  const interventionKey = useInterventionKey();
  const intervention = scenario.interventions.find(
    (i) => i.id == interventionKey,
  );

  const onUpdate = useCallback(
    (f: (x: Intervention) => Intervention) => {
      if (interventionKey) {
        updateIntervention(interventionKey, f);
      }
    },
    [updateIntervention, interventionKey],
  );

  const [showCopy, setShowCopy] = useState(false);
  const openCopy = useCallback(() => setShowCopy(true), []);
  const closeCopy = useCallback(() => setShowCopy(false), []);

  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const openConfirmDelete = useCallback(
    () => setShowConfirmDelete(true),
    [setShowConfirmDelete],
  );
  const closeConfirmDelete = useCallback(
    () => setShowConfirmDelete(false),
    [setShowConfirmDelete],
  );
  const onDeleteIntervention = useCallback(() => {
    if (interventionKey) {
      removeIntervention(interventionKey);
    }
    navigate(`/communities/${scenario.inventory}/pathways/${scenario.id}`);
  }, [navigate, removeIntervention, scenario, interventionKey]);

  if (!intervention) {
    return (
      <Navigate
        to={`/communities/${scenario.inventory}/pathways/${scenario.id}`}
      />
    );
  }
  return (
    <>
      <div
        style={{
          paddingLeft: 80,
          background: theme.color.monochrome2,
          height: '100%',
        }}
      >
        <Content padding='none' background='white' height='100%' borderLeft>
          <Container>
            <Header border>
              <Toolbar>
                <ToolbarGroup>
                  <ToolbarItem>
                    <UtilityButton
                      as={Link}
                      to={`/communities/${scenario.inventory}/pathways/${scenario.id}`}
                      icon='arrow-right'
                      display='block'
                    />
                  </ToolbarItem>
                  <Separator />
                  <ToolbarItem ellipsify>
                    <H2>{intervention.name}</H2>
                  </ToolbarItem>
                </ToolbarGroup>
                <ToolbarGroup>
                  <ToolbarItem>
                    <DropdownMenu justify='end'>
                      <DropdownMenuButton
                        as={Button}
                        variant='secondary'
                        dropdown
                        inline
                        magnitude='medium'
                      >
                        Manage intervention
                      </DropdownMenuButton>
                      <DropdownMenuList>
                        {enableCopy && (
                          <DropdownMenuItem onSelect={openCopy}>
                            Copy to scenario
                          </DropdownMenuItem>
                        )}
                        {enableCopy && <DropdownMenuSeparator />}
                        <DropdownMenuItem disabled>Export</DropdownMenuItem>
                        <DropdownMenuItem disabled>Import</DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          onSelect={openConfirmDelete}
                          variant='danger'
                        >
                          Delete intervention
                        </DropdownMenuItem>
                      </DropdownMenuList>
                    </DropdownMenu>
                  </ToolbarItem>
                </ToolbarGroup>
              </Toolbar>
            </Header>
            <Frame sizing='fill-container'>
              <InterventionEdit
                intervention={intervention}
                onUpdate={onUpdate}
              />
            </Frame>
          </Container>
        </Content>
      </div>
      {showConfirmDelete && (
        <ConfirmationDialog
          cancelText="Don't delete"
          confirmText='Delete intervention'
          onCancel={closeConfirmDelete}
          onConfirm={onDeleteIntervention}
          title={`Delete intervention "${intervention.name}"?`}
          variant='danger'
        >
          This intervention and its assumptions will be removed from your
          scenario. You will have a chance to undo this before you save your
          scenario.
        </ConfirmationDialog>
      )}
      {showCopy && (
        <CopyIntervention intervention={intervention} onClose={closeCopy} />
      )}
    </>
  );
};

export { ScenarioAssumptions };
